import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { iconList } from "../../../utils/iconList";
import useAuth from "../../../hooks/useAuth";
import { showToast } from "../../../utils/showToast";
import { posibleIntegrations } from "../../../utils/integrations";
import {
  Plus, Plug, Store, Pencil, Trash2, HelpCircle, BookOpen, Clock, Hammer,
  SlidersHorizontal, AlertTriangle, RotateCw, Loader2,
  Filter, KeyRound,
} from "lucide-react";
import {
  getJumpsellerAuthURL,
  getTiendaNubeAuthURL,
  getWixAuthURL,
  getMeliAuthURL,
  getUserIntegrations,
  integrateMercadoLibre,
} from "../../../api/integrations";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { cn } from "../../../utils/cn";
import AddCodeModal from "./modals/AddCodeModal";
import AddParisModal from "./modals/AddParisModal";
import AddStoreConnectModal from "./modals/AddStoreConnectModal";
import AddShopifyModal from "./modals/AddShopifyModal";
import AddShopifyAppModal from "./modals/AddShopifyAppModal";
import ChangeAliasModal from "./modals/ChangeAliasModal";
import DeleteIntegrationModal from "./modals/DeleteIntegrationModal";
import WooConnectionKeyModal from "./modals/WooConnectionKeyModal";
import OrderRulesDrawer from "./OrderRulesDrawer";
import ApiCredentialsSection, {
  canManageApiCredentials,
} from "./apiCredentials/ApiCredentialsSection";
import "./ModernIntegrations.scss";

export default function ModernIntegrations() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({ integrations: [] });
  const [updateUser, setUpdateUser] = useState(true);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  // Un load fallido NO puede parecer una cuenta sin integraciones: banner + retry.
  const [loadError, setLoadError] = useState(false);
  // Plataforma cuyo kickoff OAuth está en curso (spinner por fila + guardia doble click).
  const [oauthBusy, setOauthBusy] = useState(null);
  // Retorno de MercadoLibre (?code=): banner de proceso mientras corre generate-token.
  const [mlLinking, setMlLinking] = useState(false);
  // Modales propios de la vista (reemplazan al modalProvider global legacy).
  const [modal, setModal] = useState(null); // { name, props }

  // URL params (MercadoLibre OAuth)
  const urlParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const code = urlParams.get("code");
  const error = urlParams.get("error");
  // MercadoLibre devuelve `state` intacto. El operador logístico comparte el mismo
  // redirect_uri que el comercio, así que state="operator" marca que este retorno
  // NO es del comercio y debe atenderlo el callback del área admin.
  const oauthState = urlParams.get("state");

  const openLocalModal = (name, props = {}) => setModal({ name, props });
  const closeLocalModal = () => setModal(null);
  const handleModalClose = () => setUpdateUser(true);

  // Rename optimista local tras modificar alias (sin refetch, contrato legacy).
  const changeAlias = useCallback((oldAlias, newAlias) => {
    setUserData((prev) => ({
      ...prev,
      integrations: (prev.integrations || []).map((e) =>
        e.alias === oldAlias ? { ...e, alias: newAlias } : e
      ),
    }));
  }, []);

  // Acciones OAuth por integración. En éxito se navega fuera (assign) y el
  // botón queda en loading hasta el unload; solo en error se libera la guardia.
  const ACTIONS = {
    MercadoLibre: async () => {
      const values = { clientId: user?.id };
      try {
        const response = await getMeliAuthURL(values);
        if (response && response.error) {
          console.error("Error obteniendo la URL de autenticación:", response.error);
          showToast(t("Error al obtener la URL de autenticación de MercadoLibre"), false);
          setOauthBusy(null);
        } else if (response && response.authURL) {
          window.location.assign(response.authURL);
        } else {
          showToast(t("Respuesta inesperada del servidor"), false);
          setOauthBusy(null);
        }
      } catch (err) {
        console.error("Error inesperado:", err);
        showToast(t("Ocurrió un error al iniciar la integración con MercadoLibre"), false);
        setOauthBusy(null);
      }
    },
    Jumpseller: async () => {
      // El backend completa el OAuth y, al terminar, redirige el navegador a
      // esta URL del frontend con ?integration=jumpseller&status=... que la
      // página JumpsellerCallback interpreta.
      const frontendRedirectUri = `${window.location.origin}/home/integrations/jumpseller/callback`;
      try {
        const response = await getJumpsellerAuthURL({
          clientId: user?.id,
          frontendRedirectUri,
        });
        if (response && response.error) {
          console.error("Error obteniendo la URL de autenticación:", response.error);
          showToast(t("Error al obtener la URL de autenticación de Jumpseller"), false);
          setOauthBusy(null);
        } else if (response && response.authURL) {
          window.location.assign(response.authURL);
        } else {
          showToast(t("Respuesta inesperada del servidor"), false);
          setOauthBusy(null);
        }
      } catch (err) {
        console.error("Error inesperado:", err);
        showToast(t("Ocurrió un error al iniciar la integración con Jumpseller"), false);
        setOauthBusy(null);
      }
    },
    TiendaNube: async () => {
      // Mismo flujo que Jumpseller: el backend completa el OAuth y redirige el
      // navegador de vuelta a esta URL con ?integration=tiendanube&status=...
      const frontendRedirectUri = `${window.location.origin}/home/integrations/tiendanube/callback`;
      try {
        const response = await getTiendaNubeAuthURL({
          clientId: user?.id,
          frontendRedirectUri,
        });
        if (response && response.error) {
          console.error("Error obteniendo la URL de autenticación:", response.error);
          showToast(t("Error al obtener la URL de autenticación de TiendaNube"), false);
          setOauthBusy(null);
        } else if (response && response.authURL) {
          window.location.assign(response.authURL);
        } else {
          showToast(t("Respuesta inesperada del servidor"), false);
          setOauthBusy(null);
        }
      } catch (err) {
        console.error("Error inesperado:", err);
        showToast(t("Ocurrió un error al iniciar la integración con TiendaNube"), false);
        setOauthBusy(null);
      }
    },
    Wix: async () => {
      // Wix NO usa authorization-code: el backend devuelve el "install link" del
      // App Market y el intercambio lo resuelve entero él, así que acá solo se
      // pide la URL y se navega. Cada rama de error libera `oauthBusy`; si
      // alguna se olvida, el botón queda con el spinner colgado hasta recargar.
      //
      // `frontendRedirectUri` es OBLIGATORIO en WixAuthUrlDto: viaja firmado
      // dentro del `state` y es la URL a la que el backend manda el navegador al
      // terminar, con ?integration=wix&status=…&alias=|&error=. Se apunta a la
      // página propia de Wix y NO a /home/integrations porque esta vista ya lee
      // ?error= para el retorno de MercadoLibre: un fallo de Wix mostraría el
      // toast de MercadoLibre, que es peor que no decir nada.
      const frontendRedirectUri = `${window.location.origin}/home/integrations/wix/callback`;
      try {
        const response = await getWixAuthURL({ frontendRedirectUri });
        if (response && response.error) {
          console.error("Error obteniendo la URL de instalación de Wix:", response.error);
          // 503 = al backend le falta WIX_SHARE_URL_ID (el install link todavía
          // no existe). No es un fallo transitorio y reintentar no sirve: se
          // dice explícitamente en vez de un "error" genérico que invita a
          // insistir.
          showToast(
            response.status === 503
              ? t("La instalación de Wix todavía no está habilitada. Escríbenos a soporte para activarla.")
              : t("Error al obtener la URL de instalación de Wix"),
            false
          );
          setOauthBusy(null);
        } else if (response && response.authURL) {
          window.location.assign(response.authURL);
        } else {
          showToast(t("Respuesta inesperada del servidor"), false);
          setOauthBusy(null);
        }
      } catch (err) {
        console.error("Error inesperado:", err);
        showToast(t("Ocurrió un error al iniciar la integración con Wix"), false);
        setOauthBusy(null);
      }
    },
  };

  // Carga de integraciones de usuario
  useEffect(() => {
    const fetchUserData = async () => {
      if (updateUser && user) {
        try {
          setLoading(true);
          const data = await getUserIntegrations({ clientId: user?.id });
          if (data && "error" in data) {
            if (data.status === 404) {
              // 404 = la cuenta aún no tiene integraciones, no es un fallo:
              // lista vacía para que se muestre el catálogo y conecte la primera.
              setLoadError(false);
              setUserData((prev) => ({ ...prev, integrations: [] }));
            } else {
              // Fallo real (5xx/red): banner + retry, nunca aparentar cuenta vacía.
              setLoadError(true);
            }
          } else if (data && data.ok) {
            setLoadError(false);
            setUserData((prev) => ({ ...prev, integrations: data.integrations }));
          } else {
            showToast(t("Error desconocido al obtener las integraciones"), false);
            setLoadError(true);
          }
        } catch (err) {
          console.error("Error fetching integrations:", err);
          showToast(t("Error al obtener las integraciones"), false);
          setLoadError(true);
        } finally {
          setLoading(false);
          setUpdateUser(false);
        }
      }
    };
    if (user) fetchUserData();
  }, [user, updateUser]);

  // Manejo del retorno de MercadoLibre
  useEffect(() => {
    // Retorno del OAuth del OPERADOR: MercadoLibre redirige al redirect_uri del
    // comercio (/home/integrations), pero state=operator indica que el token debe
    // guardarse en logisticscompanies. Se desvía al callback del área admin
    // conservando el ?code=; ese callback hace el intercambio y redirige a
    // /admin/settings. No corre nada del flujo de comercio.
    if (code && oauthState === "operator") {
      navigate(`/admin/settings/mercadolibre/callback${window.location.search}`, {
        replace: true,
      });
      return;
    }

    const verifyMLCode = async () => {
      if (!code || !user || !user.id) return;
      setMlLinking(true);
      try {
        const data = await integrateMercadoLibre({ code, clientId: user.id });
        if (data && data.error) {
          showToast(t("Error al vincular su cuenta con MercadoLibre"), false);
          return;
        }
        if (!data || !data.ok) {
          showToast((data && data.message) || t("Error desconocido durante la integración"), false);
          return;
        }
        showToast(t("Cuenta vinculada con MercadoLibre"), true);
        setUpdateUser(true);
        navigate("/home/integrations", { replace: true });
      } catch (err) {
        console.error("Error al vincular MercadoLibre:", err);
      } finally {
        setMlLinking(false);
      }
    };

    if (code) verifyMLCode();

    if (error) {
      showToast(t("Ha ocurrido un error al vincular su cuenta con MercadoLibre"), false);
      navigate("/home/integrations", { replace: true });
    }
  }, [code, error, oauthState, user, navigate]);

  const handleIntegrationClick = (name) => {
    // TiendaNube usa el mismo kickoff OAuth: el backend arma la URL de
    // autorización y luego redirige de vuelta al portal. Sin estar en esta
    // lista, la tarjeta se renderiza y el clic no matchea ninguna rama: el
    // botón queda mudo, sin error ni traza en consola.
    if (
      name === "MercadoLibre" ||
      name === "Jumpseller" ||
      name === "TiendaNube" ||
      name === "Wix"
    ) {
      if (oauthBusy) return; // evita doble kickoff OAuth
      setOauthBusy(name);
      ACTIONS[name]();
    } else if (name === "WooCommerce" || name === "Prestashop") {
      // Connect-flow: la vinculación la inicia el plugin; el modal es la guía.
      openLocalModal("storeConnect", { platform: name });
    } else if (name === "Shopify") {
      // App oficial del App Store: la vinculación la inicia la app desde el
      // admin de Shopify; el modal es la guía. La vía por credenciales solo se
      // alcanza desde ahí, y solo con el flag de soporte encendido.
      openLocalModal("shopifyApp");
    } else if (name === "Falabella" || name === "Ripley") {
      openLocalModal("code", { platform: name });
    } else if (name === "Paris") {
      openLocalModal("paris");
    }
  };

  // Obtener integraciones vinculadas para una plataforma
  const getLinkedIntegrations = (integrationName) =>
    userData.integrations?.filter((i) => i.integrationName === integrationName) || [];

  const cleanHost = (value) =>
    String(value || "")
      .replace(/^https?:\/\//, "")
      .replace(/\/+$/, "");

  // Identidad de una cuenta vinculada en dos niveles.
  //
  // `sender_id` NO es un nombre: en Shopify/Jumpseller es el dominio (útil),
  // pero en Wix es el `instanceId`, un UUID crudo. Mostrarlo como título dejaba
  // filas del tipo "cdfa2dcb-fd30-42ad-8f0b-5b07c4885e98", que no identifican
  // nada. El alias humano manda; el dato técnico baja a segunda línea y SOLO si
  // parece un host (tiene punto). El identificador completo sigue accesible en
  // el `title` de la fila, para soporte.
  const accountIdentity = (account) => {
    const host = cleanHost(account?.shopDomain || account?.url);
    const alias = String(account?.alias || "").trim();
    const sender = String(account?.sender_id || "").trim();
    const name = alias || host || sender || t("Cuenta sin nombre");
    const candidate = host || sender;
    const handle =
      candidate && candidate !== name && candidate.includes(".")
        ? candidate
        : "";
    return { name, handle, fullRef: sender || host || "" };
  };

  // Plural natural en español (sin sufijos {{s}} interpolados).
  const accountsCountLabel = (n) =>
    n === 1 ? t("1 cuenta") : t("{{count}} cuentas", { count: n });

  // Antigüedad del vínculo: dato real del backend (`linkedOn`) que ocupa la
  // columna central. Si viene inválido no se inventa nada: la celda queda vacía.
  const linkedLabel = (account) => {
    const raw = account?.linkedOn;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    // `formatDate` fija es-CL: en inglés devolvía "15 ago 2026". El mes lo
    // pone Intl, así que tiene que seguir al idioma activo, no al de la app.
    const locale = i18n.language === "es" ? "es-CL" : i18n.language || "es-CL";
    let date;
    try {
      date = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(d);
    } catch {
      // Un locale no soportado lanza RangeError: una fecha de contexto no
      // puede tumbar la vista entera.
      date = new Intl.DateTimeFormat("es-CL", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(d);
    }
    return t("Conectada el {{date}}", { date });
  };

  const openAlias = (account) => openLocalModal("alias", { account });

  // Descripción de una línea por plataforma (solo catálogo "Disponibles").
  const DESCRIPTIONS = {
    MercadoLibre: t("Tus ventas de MercadoLibre se crean como pedidos automáticamente."),
    Falabella: t("Recibe los pedidos de tu cuenta Falabella Seller con tu código de vendedor."),
    Ripley: t("Recibe los pedidos de tu cuenta Ripley Marketplace con tu código de vendedor."),
    Paris: t("Conecta tu API key del Seller Center y recibe tus pedidos de Paris listos para despacho."),
    WooCommerce: t("Instala el plugin y sincroniza los pedidos de tu tienda WordPress."),
    Shopify: t("Instala la app oficial de Beartrack desde el Shopify App Store."),
    Jumpseller: t("Autoriza tu tienda y define reglas de creación de pedidos."),
    Prestashop: t("Instala el módulo: cotiza en el checkout con tu tarifa y sincroniza los pedidos."),
    // Sin esta entrada la fila de TiendaNube salía con el hueco de la
    // descripción vacío (la clave ya estaba traducida en los 4 idiomas).
    TiendaNube: t("Autoriza tu tienda TiendaNube y define reglas de creación de pedidos."),
    Wix: t("Instala la app de Beartrack en tu tienda Wix y define reglas de creación de pedidos."),
  };

  const CATEGORIES = [
    { id: "marketplace", label: t("Marketplaces") },
    { id: "tienda-online", label: t("Tiendas online") },
  ];

  const logo = (integration, size) => (
    <span
      className={cn(
        "mig-logo",
        size === "sm" && "mig-logo--sm",
        (integration.name === "Ripley" || integration.name === "Jumpseller") && "mig-logo--dark",
        // Wordmark oscuro sobre chip: en dark el logo queda invisible porque
        // `dark-safe-img` lo exime de la inversión global. El chip se aclara.
        ["Wix", "TiendaNube", "Prestashop"].includes(integration.name) &&
          "mig-logo--light"
      )}
      aria-hidden="true"
    >
      <img
        src={iconList[integration.icon]}
        alt=""
        className="mig-logo__img dark-safe-img"
      />
    </span>
  );

  // Particionar: conectadas (con cuentas) vs disponibles (sin cuentas / próximas)
  const connected = posibleIntegrations.filter(
    (i) => getLinkedIntegrations(i.name).length > 0
  );
  const available = posibleIntegrations.filter(
    (i) => getLinkedIntegrations(i.name).length === 0
  );
  const availableGroups = CATEGORIES.map((c) => ({
    ...c,
    items: available.filter((i) => i.category === c.id),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="w-full min-w-0 mig">
      <PageHeader
        icon={<Plug className="w-6 h-6 text-fg-muted" />}
        title={t("Integraciones")}
        subtitle={t("Conecta tus tiendas para que tus ventas se transformen en envíos automáticamente.")}
        actions={
          <Button
            variant="ghost"
            size="sm"
            className="mig-btn"
            onClick={() => navigate("/home/integrations/filtered")}
            iconLeft={<Filter size={15} aria-hidden="true" />}
          >
            {t("Pedidos filtrados")}
          </Button>
        }
      />

      {mlLinking && (
        <div className="mig-linking" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          <span>{t("Vinculando cuenta de MercadoLibre…")}</span>
        </div>
      )}

      {loading ? (
        // Skeleton con la forma real del layout nuevo: label + panel con filas.
        <section className="mig__section" aria-hidden="true">
          <span className="mig-skel mig-skel--title" />
          <div className="mig-panel">
            <span className="mig-skel mig-skel--group" />
            <div className="mig-group__grid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={`sk-${i}`} className="mig-skelrow">
                  <span className="mig-skel mig-skel--logo" />
                  <span className="mig-skelrow__lines">
                    <span className="mig-skel mig-skel--line" />
                    <span className="mig-skel mig-skel--sub" />
                  </span>
                  <span className="mig-skel mig-skel--btn" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : loadError ? (
        // Estado de error de carga: nunca mostrar la cuenta como vacía.
        <div className="mig-error" role="alert">
          <span className="mig-error__icon" aria-hidden="true">
            <AlertTriangle size={18} />
          </span>
          <div className="mig-error__text">
            <strong>{t("No pudimos cargar tus integraciones")}</strong>
            <span>{t("Tus tiendas conectadas siguen activas; es un problema al mostrarlas. Intenta nuevamente.")}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mig-btn"
            onClick={() => setUpdateUser(true)}
            iconLeft={<RotateCw size={15} aria-hidden="true" />}
          >
            {t("Reintentar")}
          </Button>
        </div>
      ) : (
        <>
          {connected.length > 0 && (
            <section className="mig__section">
              <div className="mig__section-head">
                <h2 className="mig__section-title">{t("Conectadas")}</h2>
                <Badge tone="neutral">{connected.length}</Badge>
              </div>

              {/* Un solo contenedor unificado: un bloque por plataforma, separados por divisores. */}
              <div className="mig-panel">
                {connected.map((integration) => {
                  const accounts = getLinkedIntegrations(integration.name);
                  return (
                    <article className="mig-conn" key={integration.name}>
                      <div className="mig-conn__head">
                        {logo(integration, "sm")}
                        <div className="mig-conn__id">
                          <h3 className="mig-conn__name">{integration.name}</h3>
                          {/* Un conteo no es un estado: baja a subtítulo y deja
                              el verde libre para el punto de "activa". */}
                          <span className="mig-conn__count">
                            {accountsCountLabel(accounts.length)}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mig-btn whitespace-nowrap ml-auto"
                          onClick={() => handleIntegrationClick(integration.name)}
                          loading={oauthBusy === integration.name}
                          aria-label={t("Agregar otra cuenta de {{name}}", { name: integration.name })}
                          iconLeft={<Plus size={15} aria-hidden="true" />}
                        >
                          {t("Agregar cuenta")}
                        </Button>
                      </div>

                      <ul
                        className="mig-conn__accounts"
                        aria-label={t("Cuentas vinculadas de {{name}}", { name: integration.name })}
                      >
                        {accounts.map((account, i) => {
                          const { name: label, handle, fullRef } = accountIdentity(account);
                          const linked = linkedLabel(account);
                          return (
                            // Fila semántica: <li> plano; la zona principal es un
                            // <button> real (nada de role=button con botones anidados).
                            <li key={account._id || i} className="mig-acct">
                              <button
                                type="button"
                                className="mig-acct__main"
                                onClick={() => openAlias(account)}
                                aria-label={t("Editar cuenta {{label}}", { label })}
                                title={fullRef || label}
                              >
                                {/* El badge "Activo" se repetía idéntico en cada
                                    fila (el backend no manda estado alguno): era
                                    ruido constante. Queda un punto sobre el ícono
                                    + texto solo para lectores de pantalla. */}
                                <span className="mig-acct__icon" aria-hidden="true">
                                  <Store size={15} />
                                  <span className="mig-acct__dot" />
                                </span>
                                <span className="mig-acct__id">
                                  <span className="mig-acct__name">{label}</span>
                                  {handle && (
                                    <span className="mig-acct__handle">{handle}</span>
                                  )}
                                </span>
                                <span className="sr-only">{t("Activa")}</span>
                              </button>
                              {linked && (
                                <span className="mig-acct__meta">{linked}</span>
                              )}
                              {/* Paris se lee por polling: si la API key se revoca, el
                                  único aviso que recibe el comercio es éste. */}
                              {account.lastSyncError && (
                                <span
                                  className="mig-acct__meta text-status-error"
                                  title={account.lastSyncError}
                                >
                                  <AlertTriangle size={13} aria-hidden="true" />{" "}
                                  {t("Error al leer pedidos")}
                                </span>
                              )}
                              <div className="mig-acct__actions">
                                {/* TiendaNube ya tiene su PLATFORM_PROFILES en OrderRulesDrawer,
                                    con los valores reales de payment_status de su API. Sin estar
                                    en esta lista el comercio no puede configurar reglas, y el
                                    backend crea TODOS los pedidos: no pagados y retiros incluidos. */}
                                {["Jumpseller", "WooCommerce", "Prestashop", "TiendaNube", "Shopify", "Wix"].includes(integration.name) && (
                                  <button
                                    type="button"
                                    className="mig-iconbtn"
                                    title={t("Reglas de creación")}
                                    aria-label={t("Reglas de creación de {{label}}", { label })}
                                    onClick={() =>
                                      openLocalModal("rules", {
                                        integrationId: account._id,
                                        integrationName: integration.name,
                                        alias: account.alias || label,
                                      })
                                    }
                                  >
                                    <SlidersHorizontal size={16} aria-hidden="true" />
                                  </button>
                                )}
                                {["WooCommerce", "Prestashop"].includes(integration.name) && (
                                  <button
                                    type="button"
                                    className="mig-iconbtn"
                                    title={t("Clave de conexión del plugin")}
                                    aria-label={t("Clave de conexión de {{label}}", { label })}
                                    onClick={() =>
                                      openLocalModal("wooKey", {
                                        account,
                                        platform: integration.name,
                                      })
                                    }
                                  >
                                    <KeyRound size={16} aria-hidden="true" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="mig-iconbtn"
                                  title={t("Editar alias")}
                                  aria-label={t("Editar alias de {{label}}", { label })}
                                  onClick={() => openAlias(account)}
                                >
                                  <Pencil size={16} aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="mig-iconbtn mig-iconbtn--danger"
                                  title={t("Eliminar integración")}
                                  aria-label={t("Eliminar integración {{label}}", { label })}
                                  onClick={() =>
                                    openLocalModal("delete", {
                                      integrationId: account._id,
                                      alias: account.alias || label,
                                      integrationName: integration.name,
                                      clientId: user?.id,
                                    })
                                  }
                                >
                                  <Trash2 size={16} aria-hidden="true" />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {available.length > 0 && (
            <section className="mig__section">
              <div className="mig__section-head">
                <h2 className="mig__section-title">{t("Disponibles")}</h2>
                <Badge tone="neutral">{available.length}</Badge>
              </div>

              {/* Catálogo agrupado por categoría. El label de categoría vive
                  DENTRO del panel (antes flotaba suelto encima, sin vínculo
                  visual con lo que titula) y las filas van en columnas: a
                  ancho completo la descripción quedaba a 150 caracteres de
                  medida con ~1.200px muertos hasta el botón. */}
              <div className="mig__groups">
                {availableGroups.map((group) => (
                  <section className="mig-panel" key={group.id}>
                    <h3 className="mig-group__label">{group.label}</h3>
                    <div className="mig-group__grid">
                      {group.items.map((integration) => {
                        const isActive = !!integration.value;
                        const construction = integration.status === "construction";
                        return (
                          <article
                            className={cn("mig-row", !isActive && "mig-row--soon")}
                            key={integration.name}
                            // La fila entera conecta: el hover ya la ofrecía y el
                            // clic quedaba muerto (Clarity). Sin `role="button"` a
                            // propósito — el botón de al lado sigue siendo el control
                            // de teclado y de lector de pantalla, y anidar un botón
                            // dentro de un botón es el patrón que el repo ya descartó.
                            onClick={
                              isActive
                                ? (event) => {
                                    if (event.target.closest("button")) return;
                                    handleIntegrationClick(integration.name);
                                  }
                                : undefined
                            }
                          >
                            {logo(integration)}
                            <div className="mig-row__text">
                              <h4 className="mig-row__name">{integration.name}</h4>
                              <p className="mig-row__desc">{DESCRIPTIONS[integration.name]}</p>
                            </div>
                            {isActive ? (
                              // Naranja = acento de acción, no decoración: siete
                              // CTAs primarios idénticos en columna lo anulaban.
                              // Outline en reposo, tinte de marca al pasar por la fila.
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mig-btn mig-row__cta whitespace-nowrap shrink-0"
                                onClick={() => handleIntegrationClick(integration.name)}
                                loading={oauthBusy === integration.name}
                                aria-label={t("Conectar {{name}}", { name: integration.name })}
                                iconLeft={<Plug size={15} aria-hidden="true" />}
                              >
                                {t("Conectar")}
                              </Button>
                            ) : (
                              <Badge tone="neutral" className="mig-row__status shrink-0" role="status">
                                {construction ? (
                                  <>
                                    <Hammer size={12} aria-hidden="true" /> {t("En construcción")}
                                  </>
                                ) : (
                                  <>
                                    <Clock size={12} aria-hidden="true" /> {t("Próximamente")}
                                  </>
                                )}
                              </Badge>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {!loading && canManageApiCredentials(user) && <ApiCredentialsSection />}

      {!loading && (
        <aside className="mig__help">
          <span className="mig__help-icon" aria-hidden="true">
            <HelpCircle size={18} />
          </span>
          <div className="mig__help-text">
            <strong>{t("¿Necesitas ayuda para conectar una tienda?")}</strong>
            <span>{t("Nuestro equipo de soporte puede guiarte en el proceso de vinculación.")}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mig-btn whitespace-nowrap"
            onClick={() => navigate("/home/faqs")}
            iconLeft={<BookOpen size={15} aria-hidden="true" />}
          >
            {t("Ver guías")}
          </Button>
        </aside>
      )}

      {/* Modales propios de la vista (montados solo al abrirse: estado limpio). */}
      {modal?.name === "code" && (
        <AddCodeModal
          open
          platform={modal.props.platform}
          onClose={closeLocalModal}
          onSuccess={handleModalClose}
        />
      )}
      {modal?.name === "paris" && (
        <AddParisModal open onClose={closeLocalModal} onSuccess={handleModalClose} />
      )}
      {modal?.name === "storeConnect" && (
        <AddStoreConnectModal
          open
          platform={modal.props.platform}
          onClose={() => {
            closeLocalModal();
            // Refetch al cerrar: si el comercio ya conectó desde su tienda en
            // otra pestaña, la cuenta nueva aparece sin recargar la página.
            handleModalClose();
          }}
        />
      )}
      {modal?.name === "shopifyApp" && (
        <AddShopifyAppModal
          open
          onClose={() => {
            closeLocalModal();
            // Mismo criterio que storeConnect: si el comercio ya conectó desde
            // la app en otra pestaña, la tienda aparece sin recargar.
            handleModalClose();
          }}
          // Reemplazar el modal activo cierra la guía y abre el formulario.
          onUseCredentials={() => openLocalModal("shopify")}
        />
      )}
      {modal?.name === "shopify" && (
        <AddShopifyModal
          open
          onClose={closeLocalModal}
          onSuccess={handleModalClose}
        />
      )}
      {modal?.name === "alias" && (
        <ChangeAliasModal
          open
          account={modal.props.account}
          changeAlias={changeAlias}
          onClose={closeLocalModal}
        />
      )}
      {modal?.name === "delete" && (
        <DeleteIntegrationModal
          open
          integrationId={modal.props.integrationId}
          alias={modal.props.alias}
          integrationName={modal.props.integrationName}
          clientId={modal.props.clientId}
          onClose={closeLocalModal}
          onSuccess={handleModalClose}
        />
      )}
      {modal?.name === "wooKey" && (
        <WooConnectionKeyModal
          open
          account={modal.props.account}
          platform={modal.props.platform}
          onClose={closeLocalModal}
        />
      )}
      {modal?.name === "rules" && (
        <OrderRulesDrawer
          open
          integrationId={modal.props.integrationId}
          integrationName={modal.props.integrationName}
          alias={modal.props.alias}
          onClose={closeLocalModal}
          onSaved={handleModalClose}
        />
      )}
    </div>
  );
}
